import React from 'react';
import clsx from 'clsx';
import { Label } from '../../../../../../common/Label';
import { get, isEmpty } from '../../../../../../../utils/lodash';
import { Box, Grid, makeStyles, TextField } from '@material-ui/core';
import CustomPanel from '../../../../../../common/PanelTemplateWithMandatory';
import { NOMINAL_VOLTAGE } from '../../../../../../../containers/Admin/constants';
import { SingleSelectDropdown } from '../../../../../../common/SingleSelectDropdown';
import Autocomplete from '@material-ui/lab/Autocomplete';

const useStyles = makeStyles((theme) => ({
  subRoot: {
    marginBottom: theme.spacing(4),
  },
  contactDetails: {
    padding: theme.spacing(0, 3, 4, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 1, 4, 1),
    },
  },
  inputWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
    },
  },
  addressWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '45%',
    height: '100%',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  addressWrapperWithDropdown: {
    flexDirection: 'row',
  },
  inputField: {
    minWidth: theme.spacing(30),
    margin: theme.spacing(3, 0, 0, 1),
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginTop: theme.spacing(2),
      marginLeft: 0,
    },
    '& .Mui-focused': {
      '& label': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-shrink': {
      '& label': {
        fontWeight: 400,
        color: theme.palette.primary.lightBlack,
      },
    },
    '& .MuiSelect-select': { whiteSpace: 'normal' },
  },
  inputFieldTextDropdown: {
    width: '90%',
    minWidth: theme.spacing(0),
  },
  inputFieldDropdown: {
    width: '10%',
    minWidth: theme.spacing(9),
    margin: theme.spacing(5, 0, 0, 1),
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(4),
    },
  },
}));

const ElectricalDetailsSection = (props) => {
  const classes = useStyles();
  const {
    readOnly,
    errorData,
    sectionLabel,
    preventDefault,
    getErrorText,
    getHelperText,
    electricalDetails,
    handleElectricalDetails,
  } = props;
  const {
    integrated_inverter_model = '',
    max_power_rating_stc = '',
    max_power_rating_ptc = '',
    maximum_continuous_output_power = '',
    open_circuit_voltage = '',
    max_power_voltage = '',
    short_circuit_current = '',
    max_power_current = '',
    max_ocpd_rating = '',
    module_efficiency_dc = '',
    peak_inverter_efficiency = '',
    energy_degradation = '',
    voltage_temp_coefficient = '',
    voltage_temp_unit = '',
    current_temp_coefficient = '',
    current_temp_unit = '',
    power_temp_coefficient = '',
    power_temp_unit = '',
    nominal_operating_cell_temperature = '',
    nominal_ac_voltage = '',
    max_cont_current_ac = '',
    max_string_size = '',
    inverter_efficiency = '',
  } = electricalDetails;

  return (
    <Box className={classes.subRoot}>
      <CustomPanel header={sectionLabel}>
        <Grid className={classes.contactDetails}>
          <Grid className={classes.inputWrapper}>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'max_power_rating_stc'}
                className={classes.inputField}
                label={<Label text={'Max Power Rating DC - STC (W)'} isRequired />}
                type="number"
                value={max_power_rating_stc || ''}
                onChange={(e) => handleElectricalDetails(e, 'max_power_rating_stc')}
                error={!isEmpty(get(errorData, 'max_power_rating_stc'))}
                helperText={get(errorData, 'max_power_rating_stc')}
                disabled={readOnly}
              />
            </Box>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'max_power_rating_ptc'}
                className={classes.inputField}
                label={<Label text={'Max Power Rating DC (Pmax) - PTC (W)'} />}
                type="number"
                value={max_power_rating_ptc || ''}
                onChange={(e) => handleElectricalDetails(e, 'max_power_rating_ptc')}
                error={!isEmpty(get(errorData, 'max_power_rating_ptc'))}
                helperText={get(errorData, 'max_power_rating_ptc')}
                disabled={readOnly}
              />
            </Box>
          </Grid>
          <Grid className={classes.inputWrapper}>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'open_circuit_voltage'}
                className={classes.inputField}
                label={<Label text={'Open Circuit Voltage DC (Voc) (V)'} isRequired />}
                type="number"
                value={open_circuit_voltage || ''}
                onChange={(e) => handleElectricalDetails(e, 'open_circuit_voltage')}
                error={!isEmpty(get(errorData, 'open_circuit_voltage'))}
                helperText={get(errorData, 'open_circuit_voltage')}
                disabled={readOnly}
              />
            </Box>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'max_power_voltage'}
                className={classes.inputField}
                label={<Label text={'Maximum Power Voltage DC(Vpmax) (V)'} isRequired />}
                type="number"
                value={max_power_voltage || ''}
                onChange={(e) => handleElectricalDetails(e, 'max_power_voltage')}
                error={!isEmpty(get(errorData, 'max_power_voltage'))}
                helperText={get(errorData, 'max_power_voltage')}
                disabled={readOnly}
              />
            </Box>
          </Grid>
          <Grid className={classes.inputWrapper}>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'short_circuit_current'}
                className={classes.inputField}
                label={<Label text={'Short Circuit Current DC (Isc) (A)'} isRequired />}
                type="number"
                value={short_circuit_current || ''}
                onChange={(e) => handleElectricalDetails(e, 'short_circuit_current')}
                error={!isEmpty(get(errorData, 'short_circuit_current'))}
                helperText={get(errorData, 'short_circuit_current')}
                disabled={readOnly}
              />
            </Box>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'max_power_current'}
                className={classes.inputField}
                label={<Label text={'Maximum Power Current DC (Ipmax) (A)'} isRequired />}
                type="number"
                value={max_power_current || ''}
                onChange={(e) => handleElectricalDetails(e, 'max_power_current')}
                error={!isEmpty(get(errorData, 'max_power_current'))}
                helperText={get(errorData, 'max_power_current')}
                disabled={readOnly}
              />
            </Box>
          </Grid>
          <Grid className={classes.inputWrapper}>
            <Box className={clsx(classes.addressWrapper, classes.addressWrapperWithDropdown)}>
              <TextField
                id={'voltage_temp_coefficient'}
                className={clsx(classes.inputField, classes.inputFieldTextDropdown)}
                label={<Label text={'Voltage Temp Coefficient DC (Tvoc)'} isRequired />}
                type="number"
                value={voltage_temp_coefficient || ''}
                onChange={(e) => handleElectricalDetails(e, 'voltage_temp_coefficient')}
                error={!isEmpty(get(errorData, 'voltage_temp_coefficient'))}
                helperText={get(errorData, 'voltage_temp_coefficient')}
                disabled={readOnly}
              />
              <Autocomplete
                id="voltage_temp_unit"
                disableClearable
                className={clsx(classes.inputField, classes.inputFieldDropdown)}
                options={['V/°C', '%/°C']}
                value={voltage_temp_unit || {}}
                onChange={(e, value) => handleElectricalDetails(e, 'voltage_temp_unit', value)}
                renderInput={(params) => <TextField {...params} variant="standard" />}
              />
            </Box>
            <Box className={clsx(classes.addressWrapper, classes.addressWrapperWithDropdown)}>
              <TextField
                id={'current_temp_coefficient'}
                className={clsx(classes.inputField, classes.inputFieldTextDropdown)}
                label={<Label text={'Current Temp Coefficient DC (Tisc)'} isRequired />}
                type="number"
                value={current_temp_coefficient || ''}
                onChange={(e) => handleElectricalDetails(e, 'current_temp_coefficient')}
                error={!isEmpty(get(errorData, 'current_temp_coefficient'))}
                helperText={get(errorData, 'current_temp_coefficient')}
                disabled={readOnly}
              />
              <Autocomplete
                id="current_temp_unit"
                disableClearable
                className={clsx(classes.inputField, classes.inputFieldDropdown)}
                options={['A/°C', '%/°C']}
                value={current_temp_unit || {}}
                onChange={(e, value) => handleElectricalDetails(e, 'current_temp_unit', value)}
                renderInput={(params) => <TextField {...params} variant="standard" />}
              />
            </Box>
          </Grid>
          <Grid className={classes.inputWrapper}>
            <Box className={clsx(classes.addressWrapper, classes.addressWrapperWithDropdown)}>
              <TextField
                id={'power_temp_coefficient'}
                className={clsx(classes.inputField, classes.inputFieldTextDropdown)}
                label={<Label text={'Power Temperature Coefficient DC (Tpmax)'} isRequired />}
                type="number"
                value={power_temp_coefficient || ''}
                onChange={(e) => handleElectricalDetails(e, 'power_temp_coefficient')}
                error={!isEmpty(get(errorData, 'power_temp_coefficient'))}
                helperText={get(errorData, 'power_temp_coefficient')}
                disabled={readOnly}
              />
              <Autocomplete
                id="power_temp_unit"
                disableClearable
                className={clsx(classes.inputField, classes.inputFieldDropdown)}
                options={['W/°C', '%/°C']}
                value={power_temp_unit || {}}
                onChange={(e, value) => handleElectricalDetails(e, 'power_temp_unit', value)}
                renderInput={(params) => <TextField {...params} variant="standard" />}
              />
            </Box>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'nominal_operating_cell_temperature'}
                className={classes.inputField}
                label={<Label text={'Nominal Operating Cell Temperature DC(°C)'} />}
                type="number"
                value={nominal_operating_cell_temperature || ''}
                onChange={(e) => handleElectricalDetails(e, 'nominal_operating_cell_temperature')}
                error={!isEmpty(get(errorData, 'nominal_operating_cell_temperature'))}
                helperText={get(errorData, 'nominal_operating_cell_temperature')}
                disabled={readOnly}
              />
            </Box>
          </Grid>
          <Grid className={classes.inputWrapper}>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'module_efficiency_dc'}
                className={classes.inputField}
                label={<Label text={'Module DC Efficiency (%)'} />}
                type="number"
                value={module_efficiency_dc || ''}
                onChange={(e) => handleElectricalDetails(e, 'module_efficiency_dc')}
                error={!isEmpty(get(errorData, 'module_efficiency_dc'))}
                helperText={get(errorData, 'module_efficiency_dc')}
                disabled={readOnly}
              />
            </Box>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'energy_degradation'}
                className={classes.inputField}
                label={
                  <Label
                    text={'Energy Degradation (%/year) (If not altered a default of 0.5% will be used)'}
                    capitalizeLabelText={false}
                    isRequired
                  />
                }
                type="number"
                value={energy_degradation || ''}
                onChange={(e) => handleElectricalDetails(e, 'energy_degradation')}
                error={!isEmpty(get(errorData, 'energy_degradation'))}
                helperText={get(errorData, 'energy_degradation')}
                disabled={readOnly}
              />
            </Box>
          </Grid>
          <Grid className={classes.inputWrapper}>
            <Box className={classes.addressWrapper}>
              <Autocomplete
                className={classes.inputField}
                id="integrated_inverter_model"
                freeSolo={true}
                disabled={readOnly}
                options={['Others']}
                inputValue={integrated_inverter_model}
                onInputChange={(e, value) => handleElectricalDetails(e, 'integrated_inverter_model', value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!isEmpty(get(errorData, 'integrated_inverter_model'))}
                    helperText={get(errorData, 'integrated_inverter_model')}
                    label={<Label text={'Integrated Inverter Model'} isRequired />}
                  />
                )}
              />
            </Box>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'maximum_continuous_output_power'}
                className={classes.inputField}
                label={<Label text={'Maximum Continuous Output Power (VA)'} isRequired />}
                type="number"
                value={maximum_continuous_output_power || ''}
                onChange={(e) => handleElectricalDetails(e, 'maximum_continuous_output_power')}
                error={!isEmpty(get(errorData, 'maximum_continuous_output_power'))}
                helperText={get(errorData, 'maximum_continuous_output_power')}
                disabled={readOnly}
              />
            </Box>
          </Grid>
          <Grid className={classes.inputWrapper}>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'max_ocpd_rating'}
                className={classes.inputField}
                label={<Label text={'Max OCPD Rating (A)'} />}
                type="number"
                value={max_ocpd_rating || ''}
                onChange={(e) => handleElectricalDetails(e, 'max_ocpd_rating')}
                error={!isEmpty(get(errorData, 'max_ocpd_rating'))}
                helperText={get(errorData, 'max_ocpd_rating')}
                disabled={readOnly}
              />
            </Box>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'peak_inverter_efficiency'}
                className={classes.inputField}
                label={<Label text={'Inverter Peak Efficiency (%)'} isRequired />}
                type="number"
                value={peak_inverter_efficiency || ''}
                onChange={(e) => handleElectricalDetails(e, 'peak_inverter_efficiency')}
                error={!isEmpty(get(errorData, 'peak_inverter_efficiency'))}
                helperText={get(errorData, 'peak_inverter_efficiency')}
                disabled={readOnly}
              />
            </Box>
          </Grid>
          <Grid className={classes.inputWrapper}>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'inverter_efficiency'}
                className={classes.inputField}
                label={<Label text={'Inverter CEC weighted efficiency (%)'} isRequired />}
                type="number"
                value={inverter_efficiency || ''}
                onChange={(e) => handleElectricalDetails(e, 'inverter_efficiency')}
                error={!isEmpty(get(errorData, 'inverter_efficiency'))}
                helperText={get(errorData, 'inverter_efficiency')}
                disabled={readOnly}
              />
            </Box>
            <Box className={classes.addressWrapper}>
              <SingleSelectDropdown
                dropDownLabel={<Label text={'Nominal Voltage (AC)'} />}
                dropDownOptions={NOMINAL_VOLTAGE}
                getErrorText={getErrorText}
                getHelperText={getHelperText}
                handleField={handleElectricalDetails}
                fieldId="nominal_ac_voltage"
                fieldValue={nominal_ac_voltage}
                readOnly={readOnly}
              />
            </Box>
          </Grid>
          <Grid className={classes.inputWrapper}>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'max_cont_current_ac'}
                className={classes.inputField}
                label={<Label text={'Max Cont Current AC (A)'} isRequired />}
                type="number"
                value={max_cont_current_ac || ''}
                onChange={(e) => handleElectricalDetails(e, 'max_cont_current_ac')}
                error={!isEmpty(get(errorData, 'max_cont_current_ac'))}
                helperText={get(errorData, 'max_cont_current_ac')}
                disabled={readOnly}
              />
            </Box>
            <Box className={classes.addressWrapper}>
              <TextField
                id={'max_string_size'}
                className={classes.inputField}
                label={<Label text={'Max String Size'} isRequired />}
                type="number"
                value={max_string_size || ''}
                onKeyPress={(e) => preventDefault(e)}
                onChange={(e) => handleElectricalDetails(e, 'max_string_size')}
                error={!isEmpty(get(errorData, 'max_string_size'))}
                helperText={get(errorData, 'max_string_size')}
                disabled={readOnly}
              />
            </Box>
          </Grid>
        </Grid>
      </CustomPanel>
    </Box>
  );
};

export default ElectricalDetailsSection;
